import { useMemo } from "react";
import { bool, string, number, object, shape, arrayOf, oneOf } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { CommentTypes } from "@repo/utils";
import { TEAM_ROLES } from "@utils";
import { useEstimateEditorStore } from "@tools";
import { ChatMessage } from "./ChatMessage";
import { Divider, Grid } from "@material-ui/core";
import useStyle from "./ChatThread.style";
import classnames from "classnames";

export const ChatThreadContents = observer(({
  thread,
  path,
  version,
  viewedType,
  maxDisplayReplies,
  useMaxHeight=true,
}) => {
  const editorStore = useEstimateEditorStore();
  const classes = useStyle();
  const { t } = useTranslation();

  const replies = thread?.replies || [];
  
  const projectTeamMembers = useMemo(() => (
    viewedType === CommentTypes.INTERNAL
      ? editorStore?.teamMembers
      : editorStore?.teamMembers?.filter(member => member.teamRoleId !== TEAM_ROLES.TECHNICAL)
  ), [viewedType, editorStore?.teamMembers]);

  return (
    <Grid
      item container
      className={classnames(useMaxHeight && classes.threadContainer)}
    >
      <div className="w-full">
        <ChatMessage
          isThread
          version={version}
          viewedType={viewedType}
          message={thread}
          projectTeamMembers={projectTeamMembers}
          path={path}
        />
      </div>
      {
        ( maxDisplayReplies ? replies.slice(0, maxDisplayReplies) : replies )
          ?.map((reply) => (
            <div
              key={reply.uuid}
              className="w-full"
            >
              <Divider
                variant="fullWidth"
                orientation="horizontal"
                className="mb-2"
              />
              <ChatMessage
                version={version}
                viewedType={viewedType}
                message={reply}
                projectTeamMembers={projectTeamMembers}
              />
            </div>
          ))
      }
      {
        maxDisplayReplies && replies.length > maxDisplayReplies &&
        <div className={classes.moreIndicator}>
          {t("views.editor.comments.more_replies", { amount: replies.length - maxDisplayReplies})}
        </div>
      }
    </Grid>
  );
});

ChatThreadContents.propTypes = {
  version: number.isRequired,
  viewedType: oneOf([CommentTypes.INTERNAL, CommentTypes.EXTERNAL]),
  thread: shape({
    uuid: string.isRequired,
    body: string.isRequired,
    createdAt: string.isRequired,
    replies: arrayOf(object),
    user: shape({
      uuid: string.isRequired,
      fullName: string.isRequired,
      color: string,
    })
  }).isRequired,
  maxDisplayReplies: number,
  useMaxHeight: bool,
  path: string,
};
